import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';

import BasicModal from './BasicModal';
import SelectColaborador from '../SelectColaborador';
import SelectStatusTarefa from '../SelectStatusTarefa';
import CurrencyInput from '../CurrencyInput';

import { Desdobramento } from '../../types/negocio/Desdobramento';

type Props = {
  open: boolean;
  onClose: () => void;
  onSave?: (data: Desdobramento) => void;
  desdobramento: Desdobramento;
  setDesdobramento?: React.Dispatch<React.SetStateAction<Desdobramento>>;
  modoEdicao?: boolean;
  onEditClick?: () => void;
  isNovaTarefa?: boolean;
};

const requiredFields: (keyof Desdobramento)[] = [
  'oque',
  'porQue',
  'quem',
  'como',
  'quando',
  'onde',
  'quantoCusta',
  'situacaoKanban',
];

export default function TarefaModal({
  open,
  onClose,
  onSave,
  desdobramento,
  setDesdobramento,
  modoEdicao = false,
  onEditClick,
  isNovaTarefa = false,
}: Props) {
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  // Lista de campos obrigatórios
  const camposObrigatorios = ['oque', 'porQue', 'quem', 'como', 'quando', 'onde', 'quantoCusta', 'situacaoKanban'];

  // Validar campos obrigatórios
  const validarCampos = (): boolean => {
    const novosErros: Record<string, boolean> = {};
    
    if (modoEdicao && isNovaTarefa) {
      camposObrigatorios.forEach(campo => {
        const valor = desdobramento[campo as keyof Desdobramento];
        if (!valor || valor.toString().trim() === '') {
          novosErros[campo] = true;
        }
      });
    }
    
    setErrors(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  // Limpar erros quando o modal fechar
  useEffect(() => {
    if (!open) {
      setErrors({});
    }
  }, [open]);

  const getModalTitle = () => {
    if (modoEdicao) {
      return isNovaTarefa ? "Adicionar Tarefa" : "Editar Tarefa";
    }
    return "Detalhes da Tarefa";
  };
  const handleChange = (field: keyof Desdobramento, value: string) => {
    if (!setDesdobramento) return;
    setDesdobramento(prev => ({ ...prev, [field]: value }));
    
    // Limpar erro do campo quando ele for preenchido
    if (errors[field] && value.trim() !== '') {
      setErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleMoneyChange = (value: string) => {
    const numericValue = value.replace(/[^\d.]/g, '');
    handleChange('quantoCusta', numericValue);
  };

  const handleDateChange = (date: Date | null) => {
    if (!date) return;
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const formatted = localDate.toLocaleDateString('pt-BR');
    handleChange('quando', formatted);
  };

  const isValid = requiredFields.every(
    k => (desdobramento[k] ?? '').toString().trim() !== ''
  );

  const renderCampo = (
    label: string,
    field: keyof Desdobramento,
    multiline = false
  ) => {
    const isObrigatorio = modoEdicao && isNovaTarefa && camposObrigatorios.includes(field);
    const temErro = errors[field] || false;
    
    // Campo especial: "situacaoKanban" (SelectStatusTarefa)
    if (field === 'situacaoKanban') {
      const valor = desdobramento.situacaoKanban || '';
      if (!modoEdicao && !valor.trim()) return null;

      return (
        <Box sx={{ width: modoEdicao ? '100%' : '50%', px: 1, mb: 2 }}>
          {modoEdicao ? (
            <SelectStatusTarefa
              label='Status'
              value={valor}
              onChange={(value) => handleChange('situacaoKanban', value)}
              fullWidth
              error={temErro}
              helperText={temErro ? 'Este campo é obrigatório' : ''}
              required={isObrigatorio}
            />
          ) : (
            <>
              <Typography fontSize={12} sx={{ color: "#607C90" }}>
                {label}
              </Typography>
              <Typography fontSize={16}>{valor}</Typography>
            </>
          )}
        </Box>
      );
    }
    
    if (field === 'quem') {
      const nome = desdobramento.quemNomeCompleto || '';
      if (!modoEdicao && (!desdobramento.quem || !nome.trim())) return null;

      return (
        <Box sx={{ width: modoEdicao ? '100%' : '50%', px: 1, mb: 2 }}>
          {modoEdicao ? (
            <>
              <SelectColaborador
                label='Responsável (Quem?)'
                value={desdobramento.quem || ''}
                onChange={(colab) => {
                  if (!setDesdobramento) return;
                  setDesdobramento(prev => ({
                    ...prev,
                    quem: colab?.guid ?? '',
                    quemNomeCompleto: colab?.nomeCompleto ?? '',
                  }));
                }}
                fullWidth
              />
              {temErro && (
                <Typography variant="caption" sx={{ color: 'error.main' }}>
                  Este campo é obrigatório
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography fontSize={12} sx={{ color: "#607C90" }}>
                {label}
              </Typography>
              <Typography fontSize={16}>
                {nome}
              </Typography>
            </>
          )}
        </Box>
      );
    }

    // Campo especial: "quando" (DatePicker)
    if (field === 'quando') {
      const valor = desdobramento.quando || '';
      if (!modoEdicao && !valor.trim()) return null;

      return (
        <Box sx={{ width: modoEdicao ? '100%' : '50%', px: 1, mb: 2 }}>
          {modoEdicao ? (
            <>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                <DatePicker
                  value={
                    valor ? (() => {
                      const parts = valor.split('/');
                      return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
                    })() : null
                  }
                  onChange={handleDateChange}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: { fullWidth: true, color: 'secondary' },
                  }}
                  label="Prazo (Quando?)"
                />
              </LocalizationProvider>
            </>
          ) : (
            <>
              <Typography fontSize={12} sx={{ color: "#607C90" }}>
                {label}
              </Typography>
              <Typography fontSize={16}>{valor}</Typography>
            </>
          )}
        </Box>
      );
    }

    // Campo especial: "quantoCusta" (CurrencyInput)
    if (field === 'quantoCusta') {
      const valor = desdobramento.quantoCusta || '';
      if (!modoEdicao && !valor.trim()) return null;

      const valorFormatado =
        valor
          ? `R$ ${parseFloat(valor).toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
          : '';

      return (
        <Box sx={{ width: modoEdicao ? '100%' : '50%', px: 1, mb: 2 }}>
          {modoEdicao ? (
            <CurrencyInput
              value={valor}
              size='medium'
              onChange={handleMoneyChange}
              label="Valor (Quanto custa?)"
            />
          ) : (
            <>
              <Typography fontSize={12} sx={{ color: "#607C90" }}>
                {label}
              </Typography>
              <Typography fontSize={16}>{valorFormatado}</Typography>
            </>
          )}
        </Box>
      );
    }

    // Demais campos como TextField padrão
    const valor = desdobramento[field] as string | undefined;
    if (!modoEdicao && (!valor || valor.toString().trim() === '')) return null;

    return (
      <Box sx={{ width: modoEdicao ? '100%' : '50%', px: 1, mb: 2 }}>
        {modoEdicao ? (
          <TextField
            fullWidth
            label={label}
            variant="outlined"
            color="secondary"
            value={valor || ''}
            onChange={(e) => handleChange(field, e.target.value)}
            multiline={multiline}
            rows={multiline ? 2 : 1}
            error={temErro}
            helperText={temErro ? 'Este campo é obrigatório' : ''}
            required={isObrigatorio}
          />
        ) : (
          <>
            <Typography fontSize={12} sx={{ color: "#607C90" }}>
              {label}
            </Typography>
            <Typography fontSize={16}>{valor}</Typography>
          </>
        )}
      </Box>
    );
  };

  return (
    <BasicModal
      title={getModalTitle()}
      open={open}
      onClose={onClose}
      saveButton={modoEdicao}
      onSave={async () => {
        if (modoEdicao && isNovaTarefa) {
          if (!validarCampos()) return;
        }
        if (!onSave) return;
        setSaving(true);
        try {
          await onSave(desdobramento);
        } finally {
          setSaving(false);
        }
      }}
      saveLoading={saving}
      onEdit={onEditClick}
      tela='tarefas'
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1 }}>
        {renderCampo('Tarefa (O que?)', 'oque')}
        {renderCampo('Importância (Por que?)', 'porQue')}
        {renderCampo('Status', 'situacaoKanban')}
        {renderCampo('Responsável (Quem?)', 'quem')}
        {renderCampo('Descrição (Como?)', 'como')}
        {renderCampo('Local (Onde?)', 'onde')}
        {renderCampo('Prazo (Quando?)', 'quando')}
        {renderCampo('Valor (Quanto custa?)', 'quantoCusta')}
      </Box>
    </BasicModal>
  );
}
